import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SharedDataService } from 'src/app/services/app/shared-service/shared-data.service';
import { SupportService } from 'src/app/services/core/support/support.service';
import { ToastService } from 'src/app/services/toast-service/toast.service';
import { sweetAlert } from 'src/app/utils/swal';
import {
  getRegardingOptions,
  isBrokerStaff,
  TICKET_PRIORITY_OPTIONS,
  TicketPriority
} from 'src/app/utils/support.utils';
@UntilDestroy()

@Component({
  selector: 'app-add-support-ticket',
  templateUrl: './add-support-ticket.component.html',
  styleUrls: ['./add-support-ticket.component.css']
})
export class AddSupportTicketComponent implements OnInit {
  addTicketForm!: FormGroup;
  submitted = false;
  regardingOptions: any = [];
  regarding: any;
  reference: any;
  isBrokerStaff = false;
  // broker staff: who the ticket is raised for
  selectedContact: any;
  attachments: any = [];
  isUploading = false;
  isSaving = false;
  priorityOptions = TICKET_PRIORITY_OPTIONS;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private supportService: SupportService,
    private sharedDataService: SharedDataService,
    private toastService: ToastService
  ) { }

  ngOnInit() {
    this.getUser();
    this.initForm();
  }

  initForm() {
    this.addTicketForm = this.fb.group({
      subject: ['', Validators.required],
      description: [''],
      priority: [TicketPriority.MEDIUM, Validators.required],
    });
  }

  get form() {
    return this.addTicketForm.controls;
  }

  getUser() {
    this.sharedDataService.getUser().pipe(untilDestroyed(this)).subscribe((user) => {
      this.isBrokerStaff = isBrokerStaff(user);
      if (!this.isBrokerStaff) {
        this.setRegardingOptions(user?.role?.type);
      }
    });
  }

  onSelectContact(contact: any) {
    this.selectedContact = contact;
    this.setRegardingOptions(contact?.type);
  }

  // what a ticket can be about follows the tenant, e.g. vehicles and billing only for a vendor
  setRegardingOptions(scopeType: string) {
    this.regardingOptions = getRegardingOptions(scopeType);
    this.onSelectRegarding(this.regardingOptions[0]);
  }

  onSelectRegarding(option: any) {
    this.regarding = option;
    this.setReference(null);
  }

  // a trip, company, vehicle or driver must be picked once its type is
  get isReferenceMissing() {
    return this.regarding && !this.regarding.noRecords && !this.reference;
  }

  // with a reference the subject is optional
  setReference(reference: any) {
    this.reference = reference;
    this.form['subject'].setValidators(reference ? [] : Validators.required);
    this.form['subject'].updateValueAndValidity();
  }

  onSelectFiles(event: any) {
    const files: File[] = Array.from(event.target.files);
    event.target.value = '';
    if (!files.length) {
      return;
    }

    this.isUploading = true;
    this.supportService.uploadAttachments(files).pipe(untilDestroyed(this)).subscribe((uploadedFiles: any) => {
      this.attachments.push(...uploadedFiles);
      this.isUploading = false;
    }, (err) => {
      this.isUploading = false;
      sweetAlert('Error', err, 'error', 'OK');
    });
  }

  removeAttachment(index: number) {
    this.attachments.splice(index, 1);
  }

  onSave() {
    // clicking create again while it is on its way created the same ticket again
    if (this.isSaving) {
      return;
    }
    this.submitted = true;

    if (this.addTicketForm.invalid || (this.isBrokerStaff && !this.selectedContact) || this.isReferenceMissing)
      return;

    const payload = {
      ...this.addTicketForm.value,
      attachments: this.attachments,
    };
    if (this.reference) {
      payload.referenceType = this.reference.type;
      payload.referenceId = this.reference.id;
    }
    if (this.isBrokerStaff) {
      payload.scope = { [this.selectedContact.type]: this.selectedContact.id };
    }

    this.isSaving = true;
    this.supportService.createTicket(payload).pipe(untilDestroyed(this)).subscribe((ticket: any) => {
      this.toastService.show('Ticket created');
      this.router.navigate(['/support/tickets', ticket.id]);
    }, (err) => {
      this.isSaving = false;
      sweetAlert('Error', err, 'error', 'OK');
    });
  }
}
